import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TreeItem, CategoryItem } from '../types';
import { TreeNode } from './TreeNode';
import { AddItemInput } from './AddItemInput';
import { AICardForm } from './AICardForm';
import { DropdownMenu, DropdownMenuItem, createEditAction, createDeleteAction, createAICardAction, createPracticeFolderAction } from './DropdownMenu';
import { FolderPracticeSession } from './FolderPracticeSession';
import { FolderPracticePhrasesSession } from './FolderPracticePhrasesSession';
import { PracticePickerModal } from './PracticePickerModal';
import { ContextualPracticeModal } from './ContextualPracticeModal';
import { GenerateStoryModal } from './GenerateStoryModal';
import { AddTextModal } from './AddTextModal';
import { ReviseStoryModal } from './ReviseStoryModal';
import { AIFolderGenerator } from './AIFolderGenerator';
import { handleConditionalDelete } from '../utils/deleteUtils';
import { MoveToModal } from './MoveToModal';
import { X, Folder, Loader2, FileText, Plus, Check, Move, Sparkles, Zap, BookText, Volume2, Square, ChevronDown, ChevronUp, Trash2, Pencil } from 'lucide-react';
import { apiClient, SavedStory } from '../services/api';
import { useInstance } from '../hooks/useInstance';
import { useAuth } from '../hooks/useAuth';
import { Card } from '../types/api';
import { SHOW_PRACTICE_PHRASES_IN_MENU } from '../constants/uiVisibility';
import { cardToTreeItem } from '../utils/treeUtils';
import { highlightWordsWithLinks } from '../utils/highlightWords';
import { CardDetail } from './CardDetail';
import { speak, stopSpeech } from '../utils/speechUtils';

interface FolderViewProps {
  folder: TreeItem;
  onClose: () => void;
  onRename: (id: number, newName: string) => void;
  onDelete: (id: number) => void;
  onMove: (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => Promise<void>;
  onRefresh?: () => void;
  categories?: CategoryItem[];
}

export const FolderView: React.FC<FolderViewProps> = ({
  folder,
  onClose,
  onRename,
  onDelete,
  onMove,
  onRefresh,
  categories = [],
}) => {
  const { selectedInstanceId, instancesWithRoles } = useInstance();
  const { user } = useAuth();
  const [folderData, setFolderData] = useState<TreeItem>(folder);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(folder.name);
  const [showAddInput, setShowAddInput] = useState(false);
  const [addType, setAddType] = useState<'folder' | 'card'>('card');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAICardForm, setShowAICardForm] = useState(false);
  const [showAIFolderGenerator, setShowAIFolderGenerator] = useState(false);
  const [showMoveToModal, setShowMoveToModal] = useState(false);
  const [showPracticeSession, setShowPracticeSession] = useState(false);
  const [showFolderPracticePhrases, setShowFolderPracticePhrases] = useState(false);
  const [showFolderPracticePicker, setShowFolderPracticePicker] = useState(false);
  const [showFolderContextualPractice, setShowFolderContextualPractice] = useState(false);
  const [showFolderGenerateStory, setShowFolderGenerateStory] = useState(false);
  const [showAddText, setShowAddText] = useState(false);
  const [stories, setStories] = useState<SavedStory[]>([]);
  const [isLoadingStories, setIsLoadingStories] = useState(false);
  const [expandedStoryIds, setExpandedStoryIds] = useState<Set<number>>(new Set());
  const [confirmDeleteStoryId, setConfirmDeleteStoryId] = useState<number | null>(null);
  const [deletingStoryId, setDeletingStoryId] = useState<number | null>(null);
  const [speakingStoryId, setSpeakingStoryId] = useState<number | null>(null);
  const [storyError, setStoryError] = useState<string | null>(null);
  const [editingStory, setEditingStory] = useState<SavedStory | null>(null);
  const [revisingStory, setRevisingStory] = useState<SavedStory | null>(null);
  const [openStoryCardId, setOpenStoryCardId] = useState<number | null>(null);
  const [storiesSectionVisible, setStoriesSectionVisible] = useState(false);
  const storiesSectionRef = useRef<HTMLDivElement>(null);
  const isInstanceOwner = useMemo(() => {
    if (!selectedInstanceId || !user) return false;
    const found = instancesWithRoles.find(i => i.instance.id === selectedInstanceId);
    return found?.user_role === 'owner' || found?.instance.created_by === user.id;
  }, [selectedInstanceId, instancesWithRoles, user]);
  const isRootFolder = folder.parent_id === null;


  useEffect(() => {
    return () => { stopSpeech(); };
  }, []);

  useEffect(() => {
    if (selectedInstanceId !== null) {
      loadFolderData();
      loadStories();
    }
  }, [folder.id, selectedInstanceId]);

  const loadStories = async () => {
    setIsLoadingStories(true);
    try {
      const data = await apiClient.listFolderStories(folder.id);
      setStories(data.items);
    } catch {
      // silently fail
    } finally {
      setIsLoadingStories(false);
    }
  };

  const handleDeleteStory = async (storyId: number) => {
    setDeletingStoryId(storyId);
    setStoryError(null);
    try {
      await apiClient.deleteFolderStory(folder.id, storyId);
      setStories(prev => prev.filter(s => s.id !== storyId));
      setConfirmDeleteStoryId(null);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setStoryError('You can only delete texts you created.');
      } else {
        setStoryError('Could not delete the text. Please try again.');
      }
    } finally {
      setDeletingStoryId(null);
    }
  };

  // Sync editName with folder.name when folder changes
  useEffect(() => {
    if (!isEditing) {
      setEditName(folderData.name);
    }
  }, [folderData.name, isEditing]);

  useEffect(() => {
    const el = storiesSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStoriesSectionVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [storiesSectionRef.current]);

  const loadFolderData = async () => {
    if (selectedInstanceId === null) return;
    try {
      setIsLoading(true);
      // Get the folder's direct children
      const response = await apiClient.getCardsByParent(folder.id, { instance_id: selectedInstanceId });
      const children: TreeItem[] = response.items.map((card: Card) => cardToTreeItem(card));
      
      setFolderData({
        ...folder,
        children: children,
      });
    } catch (err: any) {
      console.error('Error loading folder data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (id: number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      
      // Update the tree items with new expansion state
      const updateTreeExpansion = (items: TreeItem[]): TreeItem[] => {
        return items.map((item) => ({
          ...item,
          isExpanded: newSet.has(item.id),
          children: item.children ? updateTreeExpansion(item.children) : [],
        }));
      };
      
      setFolderData((prev) => ({
        ...prev,
        children: updateTreeExpansion(prev.children),
      }));
      
      return newSet;
    });
  };

  const handleAddChildToFolder = async (parentId: number, type: 'folder' | 'card', name: string) => {
    try {
      await apiClient.createCard({
        name,
        parent_id: parentId,
        is_folder: type === 'folder',
        category_id: folder.category_id,
      });

      // Refresh the folder data
      await loadFolderData();
      if (onRefresh) {
        onRefresh();
      }
    } catch (err: any) {
      console.error('Error adding child to folder:', err);
      const errorMessage = err.message || err.response?.data?.detail || 'Failed to add item';
      alert(`Failed to add item: ${errorMessage}`);
    }
  };

  const handleRename = async () => {
    if (!editName.trim()) {
      setEditName(folderData.name);
      setIsEditing(false);
      return;
    }

    if (editName.trim() === folderData.name) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await apiClient.updateCard(folderData.id, { name: editName.trim() });
      onRename(folderData.id, editName.trim());
      setFolderData({ ...folderData, name: editName.trim() });
      setIsEditing(false);
      if (onRefresh) {
        onRefresh();
      }
    } catch (err: any) {
      console.error('Error renaming folder:', err);
      setEditName(folderData.name);
      alert('Failed to rename folder. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditName(folderData.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRename();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && (relatedTarget.closest('.edit-actions') || relatedTarget.closest('button'))) {
      return;
    }
    setTimeout(() => {
      if (isEditing) {
        handleRename();
      }
    }, 200);
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await handleConditionalDelete(
        folderData,
        () => {
          onDelete(folderData.id);
          onClose();
        },
        (error) => {
          console.error('Error deleting folder:', error);
          alert('Failed to delete folder');
        }
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddChild = async (name: string) => {
    try {
      await apiClient.createCard({
        name,
        parent_id: folderData.id,
        is_folder: addType === 'folder',
        category_id: folderData.category_id,
      });

      await loadFolderData();
      setShowAddInput(false);
      if (onRefresh) {
        onRefresh();
      }
    } catch (err: any) {
      console.error('Error adding child:', err);
      alert('Failed to add item');
    }
  };

  const handleAddFolder = () => {
    setAddType('folder');
    setShowAddInput(true);
  };

  const handleAddCard = () => {
    setAddType('card');
    setShowAddInput(true);
  };

  const handleAICardSuccess = (cardId: number) => {
    setShowAICardForm(false);
    loadFolderData();
    if (onRefresh) {
      onRefresh();
    }
  };

  // Create dropdown menu items for folder
  const getFolderDropdownItems = (): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [];

    if (SHOW_PRACTICE_PHRASES_IN_MENU) {
      items.push({
        id: 'practice-phrases',
        label: 'Practice Phrases',
        icon: <Sparkles className="h-4 w-4" />,
        onClick: () => setShowFolderPracticePhrases(true),
      });
    }
    // Temporarily hidden: Practice Folder feature
    // items.push(createPracticeFolderAction(() => setShowPracticeSession(true)))

    // Only show Move To if user is instance owner
    if (isInstanceOwner) {
      items.push(
        {
          id: 'move',
          label: 'Move To...',
          icon: <Move className="h-4 w-4" />,
          onClick: () => setShowMoveToModal(true),
        },
        createEditAction(() => setIsEditing(true)),
        createDeleteAction(handleDelete)
      );
    }
    
    return items;
  };

  // Count items
  const totalItems = folderData.children.length;
  const folderCount = folderData.children.filter(child => child.is_folder).length;
  const cardCount = folderData.children.filter(child => !child.is_folder).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="hidden sm:flex p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <Folder className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    className="flex-1 border border-blue-300 rounded-md px-3 py-1 text-lg sm:text-xl font-bold bg-white text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                    disabled={isSaving}
                  />
                  <div className="edit-actions flex items-center space-x-1">
                    <button
                      onClick={handleRename}
                      disabled={isSaving || !editName.trim()}
                      className="p-1.5 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Save"
                    >
                      <Check className="h-5 w-5 text-green-600" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="p-1.5 hover:bg-red-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Cancel"
                    >
                      <X className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-lg sm:text-xl font-bold text-blue-900 truncate">
                    {folderData.name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1">
                    <p className="text-xs sm:text-sm text-blue-600">
                      {totalItems} {totalItems === 1 ? 'item' : 'items'}
                    </p>
                    {folderCount > 0 && (
                      <p className="text-xs sm:text-sm text-blue-600 flex items-center">
                        <Folder className="h-3 w-3 mr-1" />
                        {folderCount} {folderCount === 1 ? 'folder' : 'folders'}
                      </p>
                    )}
                    {cardCount > 0 && (
                      <p className="text-xs sm:text-sm text-blue-600 flex items-center">
                        <FileText className="h-3 w-3 mr-1" />
                        {cardCount} {cardCount === 1 ? 'card' : 'cards'}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Add buttons - only for instance owners */}
          {!isEditing && isInstanceOwner && (
            <div className="flex items-center space-x-2 mr-2">
              {isRootFolder && (
                <button
                  onClick={handleAddFolder}
                  className="hidden sm:flex p-2 hover:bg-blue-100 rounded-lg bg-blue-50 transition-colors"
                  title="Add subfolder"
                >
                  <Folder className="h-4 w-4 text-blue-600" />
                </button>
              )}
              <button
                onClick={handleAddCard}
                className="hidden sm:flex p-2 hover:bg-gray-200 rounded-lg bg-gray-100 transition-colors"
                title="Add card"
              >
                <Plus className="h-4 w-4 text-gray-700" />
              </button>
            </div>
          )}

          {!isEditing && (
            <button
              onClick={() => setShowFolderPracticePicker(true)}
              className="p-2 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors flex-shrink-0 ml-2"
              title="Actions"
            >
              <Zap className="h-4 w-4 text-amber-600" />
            </button>
          )}

          {/* Actions dropdown menu */}
          {!isEditing && (
            <div className="mr-2">
              <DropdownMenu
                items={getFolderDropdownItems()}
                className="opacity-100"
                disabled={isDeleting}
              />
            </div>
          )}

          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-100 rounded-lg transition-colors flex-shrink-0 ml-2"
            title="Close"
          >
            <X className="h-5 w-5 text-blue-600" />
          </button>
        </div>

        {/* Add item input */}
        {showAddInput && (
          <div className="px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-100">
            <AddItemInput
              parentId={folderData.id}
              type={addType}
              onSubmit={handleAddChild}
              onCancel={() => setShowAddInput(false)}
              placeholder={`Add ${addType}...`}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading folder...</span>
            </div>
          ) : folderData.children.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Folder className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No items in this folder</p>
              <p className="text-sm text-center mb-4">
                {isRootFolder ? 'Add subfolders or cards to get started' : 'Add cards to get started'}
              </p>
              {isInstanceOwner && (
                <div className="flex items-center justify-center space-x-2">
                  {isRootFolder && (
                    <button
                      onClick={handleAddFolder}
                      className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      Add Subfolder
                    </button>
                  )}
                  <button
                    onClick={handleAddCard}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Add Card
                  </button>
                  <button
                    onClick={() => setShowAICardForm(true)}
                    className="px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                  >
                    <Sparkles className="h-4 w-4 inline mr-1" />
                    Add with AI
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {folderData.children.map((item) => (
                <TreeNode
                  key={item.id}
                  item={item}
                  onToggle={handleToggle}
                  onRename={onRename}
                  onDelete={onDelete}
                  onAddChild={handleAddChildToFolder}
                  onMove={onMove}
                  level={0}
                  categoryId={folder.category_id}
                  categories={categories}
                  isInstanceOwner={isInstanceOwner}
                />
              ))}
            </div>
          )}

          {/* Saved Texts */}
          <div ref={storiesSectionRef} className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Saved Texts</h3>
              {isInstanceOwner && (
                <button
                  type="button"
                  onClick={() => setShowAddText(true)}
                  className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                  title="Add text"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
            {isLoadingStories ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
              </div>
            ) : stories.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No texts saved yet.</p>
            ) : (
              <div className="space-y-3">
                {storyError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{storyError}</div>
                )}
                {stories.map(story => (
                  <div key={story.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      className="flex items-start justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedStoryIds(prev => {
                        const next = new Set(prev);
                        next.has(story.id) ? next.delete(story.id) : next.add(story.id);
                        return next;
                      })}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{story.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(story.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="p-1.5 text-gray-400">
                          {expandedStoryIds.has(story.id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const isPlaying = speakingStoryId === story.id;
                            if (isPlaying) {
                              stopSpeech();
                              setSpeakingStoryId(null);
                            } else {
                              speak(story.story, {
                                onEnd: () => setSpeakingStoryId(null),
                                onError: () => setSpeakingStoryId(null),
                              });
                              setSpeakingStoryId(story.id);
                            }
                          }}
                          className={`p-1.5 rounded-full transition-colors ${speakingStoryId === story.id ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                          title={speakingStoryId === story.id ? 'Stop' : 'Read aloud'}
                        >
                          {speakingStoryId === story.id ? <Square className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                        </button>
                        {story.user_created === user?.id && (
                          confirmDeleteStoryId === story.id ? (
                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                              <span className="text-xs text-gray-500">Sure?</span>
                              <button
                                onClick={() => handleDeleteStory(story.id)}
                                disabled={deletingStoryId === story.id}
                                className="text-xs text-red-600 hover:underline disabled:opacity-50"
                              >
                                {deletingStoryId === story.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Yes'}
                              </button>
                              <button
                                onClick={() => setConfirmDeleteStoryId(null)}
                                className="text-xs text-gray-500 hover:underline"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); setRevisingStory(story); }}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                                title="Revise"
                              >
                                <Sparkles className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingStory(story); }}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                                title="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteStoryId(story.id); }}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )
                        )}
                      </div>
                    </div>
                    {expandedStoryIds.has(story.id) && (
                      <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{highlightWordsWithLinks(story.story, story.words_used, setOpenStoryCardId)}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Saved texts pill */}
        {stories.length > 0 && !isLoadingStories && !storiesSectionVisible && (
          <div className="flex justify-center py-2.5 border-t border-gray-100 bg-white rounded-b-lg">
            <button
              onClick={() => storiesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full transition-colors"
            >
              <BookText className="h-3.5 w-3.5" />
              {stories.length} saved text{stories.length !== 1 ? 's' : ''} ↓
            </button>
          </div>
        )}

        {/* AI Card Form Modal */}
        {showAICardForm && (
          <AICardForm
            parentId={folderData.id}
            parentName={folderData.name}
            onClose={() => setShowAICardForm(false)}
            onSuccess={handleAICardSuccess}
          />
        )}

        {/* Move To Modal */}
        {showMoveToModal && categories.length > 0 && (
          <MoveToModal
            item={folderData}
            categories={categories}
            onClose={() => setShowMoveToModal(false)}
            onMove={onMove}
          />
        )}

        {/* Folder Practice Session */}
        {showPracticeSession && (
          <FolderPracticeSession
            folderId={folderData.id}
            folderName={folderData.name}
            categoryId={folderData.category_id}
            categoryName={categories.find(cat => cat.id === folderData.category_id)?.name}
            onClose={() => setShowPracticeSession(false)}
          />
        )}

        {showFolderPracticePhrases && (
          <FolderPracticePhrasesSession
            folderId={folderData.id}
            folderName={folderData.name}
            onClose={() => setShowFolderPracticePhrases(false)}
          />
        )}

        {showFolderPracticePicker && (
          <PracticePickerModal
            categoryName={folderData.name}
            onClose={() => setShowFolderPracticePicker(false)}
            onFlashcards={() => setShowPracticeSession(true)}
            onContextual={() => setShowFolderContextualPractice(true)}
            onGenerateText={() => setShowFolderGenerateStory(true)}
            hasCards={cardCount > 0}
            isOwner={isInstanceOwner}
            onAICard={isInstanceOwner ? () => setShowAICardForm(true) : undefined}
            onAIFolder={isInstanceOwner ? () => setShowAIFolderGenerator(true) : undefined}
            onAddText={isInstanceOwner ? () => setShowAddText(true) : undefined}
          />
        )}

        {showAddText && (
          <AddTextModal
            folderId={folderData.id}
            onClose={() => setShowAddText(false)}
            onSaved={loadStories}
          />
        )}

        {openStoryCardId && (
          <CardDetail
            cardId={openStoryCardId}
            onClose={() => setOpenStoryCardId(null)}
            onSave={() => {}}
          />
        )}

        {editingStory && (
          <AddTextModal
            folderId={folderData.id}
            storyId={editingStory.id}
            initialTitle={editingStory.title}
            initialText={editingStory.story}
            onClose={() => setEditingStory(null)}
            onSaved={loadStories}
          />
        )}

        {revisingStory && (
          <ReviseStoryModal
            storyId={revisingStory.id}
            storyText={revisingStory.story}
            onClose={() => setRevisingStory(null)}
            onReplace={async (text, wordsUsed) => {
              await apiClient.updateFolderStory(folderData.id, revisingStory.id, { story: text, words_used: wordsUsed.length ? wordsUsed : undefined });
              loadStories();
            }}
          />
        )}

        {showAIFolderGenerator && folderData.category_id != null && (
          <AIFolderGenerator
            categoryId={folderData.category_id}
            parentId={folderData.id}
            userEmail={user?.email}
            onClose={() => setShowAIFolderGenerator(false)}
            onSuccess={() => {
              if (onRefresh) onRefresh();
            }}
          />
        )}

        {showFolderContextualPractice && (
          <ContextualPracticeModal
            folderId={folderData.id}
            categoryName={folderData.name}
            leafCardCount={cardCount}
            onClose={() => setShowFolderContextualPractice(false)}
          />
        )}

        {showFolderGenerateStory && (
          <GenerateStoryModal
            folderId={folderData.id}
            categoryName={folderData.name}
            onClose={() => setShowFolderGenerateStory(false)}
            onStorySaved={loadStories}
          />
        )}

      </div>
    </div>
  );
};

